import {
  Component,
  AfterViewInit,
  ElementRef,
  ViewChild,
  Renderer2,
  inject,
  HostListener,
  HostBinding,
  EventEmitter,
  Output,
  OnInit,
  OnChanges,
  OnDestroy,
  Input,
  ViewChildren,
  QueryList
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {MaterialModule} from '@app/material.module';
import {FormControl, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatButtonToggleModule} from '@angular/material/button-toggle';
import {MatIconModule} from '@angular/material/icon';
import {TablerIconsModule} from 'angular-tabler-icons';
import {ChromePickerComponent, COLOR_PICKER_CONFIG, ColorPickerControl, Color} from '@iplab/ngx-color-picker';
import {provideAnimations} from '@angular/platform-browser/animations';
import {configuration} from '@core/data/config';
import {ShapeDraw} from '@core/enums/shape_draw';
import {ShapeDrawService} from '@services/shape-draw/shape-draw.service';
import * as fabric from 'fabric';
import {debounceTime, fromEvent, Subject, takeUntil} from 'rxjs';
import { MatDividerModule } from '@angular/material/divider';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltip, MatTooltipDefaultOptions } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';

/** Custom options the configure the tooltip's default show/hide delays. */
export const myCustomTooltipDefaults: MatTooltipDefaultOptions = {
  showDelay: 100,
  hideDelay: 100,
  touchendHideDelay: 100,
};

@Component({
  selector: 'app-draw',
  imports: [CommonModule, MaterialModule, FormsModule, ReactiveFormsModule, TablerIconsModule, ChromePickerComponent, TranslateModule],
  standalone: true,
  providers: [provideAnimations(),{provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: myCustomTooltipDefaults}],
  templateUrl: './draw.component.html',
  styleUrls: ['./draw.component.scss']
})
export class DrawComponent implements AfterViewInit, OnInit, OnChanges, OnDestroy {
  @ViewChild('canvas', { static: false }) canvasElement!: ElementRef;
  @ViewChild('canvascontainer', { static: false }) canvascontainer!: ElementRef;
  @ViewChild('colorPickerRef', {static: true}) colorPickerRef: ElementRef | undefined;
  @ViewChild('buttonRef', { static: true }) buttonRef: ElementRef | undefined;
  @ViewChildren('tooltip') tooltips!: QueryList<MatTooltip>;

  @Input() imageBase64!: string;
  @Input() isEditMode!: boolean;
  @Output() canvasReady = new EventEmitter<fabric.Canvas>();
  @Output() croppedData = new EventEmitter<string>();
  @Output() onClickSave = new EventEmitter<boolean>();
  public shapeDraw = ShapeDraw;
  public colorControl = new ColorPickerControl();
  private drawService: ShapeDrawService = inject(ShapeDrawService);
  private renderer: Renderer2 = inject(Renderer2);

  optionsSizeValue = configuration.font_size_list;
  sizeValue = new FormControl(configuration.default_size_stroke_draw);
  sizeFontValue = new FormControl(configuration.default_font_size_draw);
  selectedShape = this.drawService.selectedShapeControl;

  disabled = false;
  max = 20;
  min = 1;
  showTicks = false;

  step = 1;
  isVisibleColorPicker = false;
  thumbLabel = true;
  selectedColor: string = configuration.default_color_draw;

  private destroy$ = new Subject<void>();
  contrastColor: string = 'rgb(255,255,255)';

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.selectedShape.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: any) => {
        if (value === this.shapeDraw.STOP_CROP) {
          this.stopCropping();
        } else {
          this.drawService.setShape(value);
        }
      });

    this.renderer.listen('document', 'keydown', (event: KeyboardEvent) => {
      const activeObject = this.drawService.getCanvasElement().getActiveObject();
      // Se si sta editando del testo (IText/Textbox in editing), il Backspace/Delete
      // deve cancellare i caratteri dentro la casella: lasciamo gestire a Fabric e
      // non rimuoviamo l'intero oggetto dal canvas.
      const isEditingText = !!activeObject && (activeObject as any).isEditing === true;
      if ((event.key === 'Backspace' || event.key === 'Delete') && activeObject && !isEditingText) {
        this.drawService.clearCanvas();
      } else if(event.key === 'Cmd' || event.key === 'Control' || event.key === 'Meta') {
        this.drawService.setIsMultipleKeyDown(true);
      }
    });

    this.renderer.listen('document', 'keyup', (event: KeyboardEvent) => {
      if(event.key === 'Cmd' || event.key === 'Control' || event.key === 'Meta') {
        this.drawService.setIsMultipleKeyDown(false);
      }
    });

    fromEvent(window, 'resize')
    .pipe(
      debounceTime(100),
      takeUntil(this.destroy$)
    )
    .subscribe(() => this.setCanvasContainerHeight());
  }

  ngOnChanges(changes: any): void {
    // if (changes['isEditMode'] && changes['isEditMode'].currentValue) {
    //   console.log('isEditMode changed')
    // }
    if (changes['imageBase64'] && changes['imageBase64'].currentValue) {
      if (this.imageBase64) {
        this.drawService.setImageBackground(this.imageBase64);
      }
      this.canvasReady.emit(this.drawService.getCanvasElement());
    }

  }

  ngAfterViewInit(): void {
    if(this.canvascontainer?.nativeElement){
    this.setCanvasContainerHeight();
    }
    this.drawService.initCanvas(this.canvasElement);
    if (this.imageBase64) {
      this.drawService.setImageBackground(this.imageBase64);
    }
    this.canvasReady.emit(this.drawService.getCanvasElement());
  }

  private setCanvasContainerHeight(): void {
    if (this.canvascontainer?.nativeElement) {
      const height = window.innerHeight - this.canvascontainer.nativeElement.offsetTop - 244;
      this.renderer.setStyle(this.canvascontainer.nativeElement, 'height', `${height}px`);
    }
  }

  onSizeChange(event: any) {
    this.drawService.onChangeStrokeWidth(this.sizeValue.value as number);
  }

  onChangeFontSize(event: any) {
    this.drawService.onChangeFontSize(this.sizeFontValue.value as number);
  }

  changeColor(color: string): void {
    this.selectedColor = color;
    this.drawService.onChangeColor(this.selectedColor);
    this.contrastColor = this.getContrastColorFromRgb(this.selectedColor);
  }
  getContrastColorFromRgb(rgba: string): string {
    const rgbaValues = rgba.match(/rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*([\d.]*)\)/);
    if (rgbaValues) {
      const r = parseInt(rgbaValues[1]);
      const g = parseInt(rgbaValues[2]);
      const b = parseInt(rgbaValues[3]);
      const yiq = (r * 299 + g * 587 + b * 114) / 1000;
      return yiq >= 128 ? '#000000' : '#FFFFFF';
    }
    return '#000000'; // default fallback

  }

  getContrastColor(hex: string): string {
    if (!hex) return '#000'; // fallback
    hex = hex.replace('#', '');

    // Expand shorthand form (e.g. "03F") to full form ("0033FF")
    if (hex.length === 3) {
      hex = hex.split('').map(x => x + x).join('');
    }

    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    const luminance = (0.299 * r + 0.587 * g + 0.114 * b);

    return luminance > 186 ? '#000000' : '#FFFFFF';
  }

  public showColorPicker() {
    this.isVisibleColorPicker = !this.isVisibleColorPicker;
  }

  public applyClick(event: MouseEvent): void {
    event.stopPropagation();
    this.selectedColor = this.colorControl.value.toRgbaString();
    this.changeColor(this.selectedColor)
    this.isVisibleColorPicker = false;
  }

  public discardClick(event: MouseEvent): void {
    event.stopPropagation();
    this.isVisibleColorPicker = false;
  }

  reset(): void {
    this.selectedShape.setValue(this.shapeDraw.POINTER);
  }

  removeObjectCanvas() {
    this.drawService.clearCanvas();
  }

  downloadImage(): void {
    const link = document.createElement('a');
    link.href = this.drawService.saveImage();
    link.download = 'image.png';
    link.click();
  }

  stopCropping() {
    this.drawService.setShape(this.shapeDraw.POINTER);
    const croppedDataURL = this.drawService.stopCrop();
    if (croppedDataURL) {
      this.croppedData.emit(croppedDataURL);
    }
    this.drawService.setShape(this.shapeDraw.POINTER);
  }

  save() {
    this.onClickSave.emit(true);
  }

  destroyTooltip(event: MouseEvent) {
    // Trova il tooltip associato all'elemento
    
    const button = event.currentTarget as HTMLElement;
    this.tooltips.forEach(tooltip => {
      // Nascondi il tooltip
      tooltip.hide();
      
      // IMPORTANTE: Distruggi completamente l'overlay
      const overlayRef = (tooltip as any)._overlayRef;
      if (overlayRef) {
        overlayRef.dispose();
        (tooltip as any)._overlayRef = null;
      }
    });
  }


}
